import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { useFormState, useValidate } from '@flywire/react-hooks';

import { useDispatch } from 'store/configureStore';

import { addCoupon } from 'actions';
import { Button } from '@flywire/headlessui-tailwindcss';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { TextInput } from 'components/input/TextInput/TextInput';
import { ReactComponent as InfoIcon } from 'icons/ico-info-new.svg';
import type { RootState } from 'reducers/types';
import { isFetching, isPayingFromChina, isPayingFromIndia } from 'selectors';
import { useTranslations } from 'utils';
import { isHttpErrorWithStatus } from 'utils/errors/errors';

import './Coupon.scss';

const Coupon = () => {
  const i18n = useTranslations();
  const dispatch = useDispatch();
  const { values, update } = useFormState({ couponCode: '' });
  // @ts-expect-error not properly typed
  const { isValid } = useValidate(values, {
    couponCode: { presence: { allowEmpty: false } },
  });
  const [serverError, setServerError] = useState('');

  const payingFromChina = useSelector(isPayingFromChina);
  const payingFromIndia = useSelector(isPayingFromIndia);
  const orderIsFetching = useSelector((state: RootState) =>
    isFetching(state, 'order'),
  );

  const handleChange = (name: string, value: string) => {
    update(name, value);
  };
  const handleClick = async () => {
    const { couponCode } = values;
    try {
      await dispatch(addCoupon(couponCode as string));
    } catch (error) {
      handleError(error);
    }
  };

  const handleError = (error: unknown) => {
    if (isHttpErrorWithStatus(error) && error.status === 422) {
      setServerError(i18n.t('coupon.serverError.invalid'));
    } else {
      setServerError(i18n.t('coupon.serverError.unable'));
    }
  };

  const tooltipText = payingFromChina
    ? i18n.t('coupon.tooltip.text.cn')
    : payingFromIndia
      ? i18n.t('coupon.tooltip.text.in')
      : null;

  if (orderIsFetching) {
    return null;
  }

  return (
    <>
      {tooltipText && (
        <dt className="Coupon-tooltip" data-testid="CouponTooltip">
          {i18n.t('coupon.tooltip.label')}
          <Tooltip
            data-testid="CouponTooltipContent"
            defaultMessage={tooltipText}
          >
            <InfoIcon height="14" width="14" aria-hidden="true" />
          </Tooltip>
        </dt>
      )}

      <dd className="w-full flex flex-row gap-4" data-testid="CouponBox">
        <TextInput
          label={i18n.t('coupon.input.label')}
          name="couponCode"
          className="w-full"
          onChange={handleChange}
          value={values.couponCode as string}
          data-testid="couponInput"
          error={serverError}
          disabled={orderIsFetching}
        />
        <Button
          tone="secondary"
          onClick={handleClick}
          data-testid="couponButton"
          disabled={!isValid || orderIsFetching}
          className="text-sm min-w-28 h-12 border-gray-500 disabled:cursor-not-allowed disabled:text-blue-600/40 uppercase font-medium"
        >
          {i18n.t('coupon.button.text')}
        </Button>
      </dd>
    </>
  );
};

export { Coupon };
